import { action, observable, makeObservable } from "mobx";
import ProductAPI from "../api/ProductAPI";
import {
  Product,
  ProductImage,
  LastSoldProduct,
  ProductSEODetails,
  ProductSourcingDetails,
} from "../../../models/Products/Product";
import { GetProductsParams } from "../../../models/Products/GetProductsParams";
import { Category } from "../../../models/Categories/types";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { Vendor } from "../../../models/Vendors/Vendor";
import { ProductOption } from "../../../models/Products/ProductOption";
import {
  ProductVariant,
  ProductVariantImages,
} from "../../../models/Products/ProductVariant";
import { OrderProduct } from "../../../models/Order/OrderProduct";
import { PrintingType } from "../../../models/Products/PrintingTypes";
import { PurchaseRequestProductInsights } from "../../../models/PurchaseRequest/PurchaseRequestProductInsights";
import { ActivityLog } from "../../../models/Activity/ActivityLog";

export class ProductStore {
  productAPI: ProductAPI;
  limit: number;
  total_pages: number;
  total_count: number;
  products: Product[];
  categories: Category[];
  error: string | null = null;
  productDetails: Product;
  originalProduct: Product;
  productOptions: ProductOption[];
  printingTypes: PrintingType[];
  lastSoldProducts: LastSoldProduct[];
  purchaseRequestInsights: PurchaseRequestProductInsights[];
  activityLogs: ActivityLog[];
  selectedSizeChartFile: File | null = null;
  openSections: {
    product: boolean;
    sourcing: boolean;
    seo: boolean;
    media: boolean;
  };

  constructor() {
    this.productAPI = new ProductAPI();
    this.products = [];
    this.categories = [];
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
    this.productOptions = [];
    this.productDetails = { image_urls: [] } as Product;
    this.originalProduct = { image_urls: [] } as Product;
    this.printingTypes = [];
    this.lastSoldProducts = [];
    this.purchaseRequestInsights = [];
    this.activityLogs = [];
    this.openSections = {
      product: true,
      sourcing: false,
      seo: false,
      media: false,
    };

    makeObservable(this, {
      products: observable,
      productDetails: observable,
      productOptions: observable,
      printingTypes: observable,
      lastSoldProducts: observable,
      selectedSizeChartFile: observable,
      purchaseRequestInsights: observable,
      activityLogs: observable,
      openSections: observable,
      getCategories: action,
      getProducts: action,
      setProductPodCatalogStatus: action,
      error: observable,
      reset: action,
      updateName: action,
      updateZohoID: action,
      discardChanges: action,
      updateCategoryName: action,
      updateCostPrice: action,
      updateSellingPrice: action,
      updateHSNCode: action,
      updateDescription: action,
      removeImage: action,
      addImages: action,
      updatedVendors: action,
      updateCategory: action,
      updateGSTCode: action,
      updateLocalProductDetails: action,
      updateProductStatus: action,
      clearProductDetails: action,
      addVariantImages: action,
      removeVariantImage: action,
      updateSEOTitle: action,
      updateSEODescription: action,
      updateDisplay: action,
      updateFastProcurement: action,
      updatePreferredPrintingTypeId: action,
      updatePreferredVendorId: action,
      updateProductOptions: action,
      updateVariantImage: action,
      discardVariantImagesChanges: action,
      updateSKU: action,
      getProductOptions: action,
      createProduct: action,
      resetProductOptions: action,
      toggleSection: action,
      updateProductSEO: action,
      updateProductSource: action,
      addSizeChartImage: action,
      removeSizeChartImage: action,
      setSelectedSizeChartFile: action,
      clearSelectedSizeChartFile: action,
      getPrintingTypes: action,
      setDefaultImage: action,
      fetchPurchaseRequestInsights: action,
      getProductActivityLogs: action,
      clearActivityLogs: action,
    });
  }

  reset = () => {
    this.products = [];
    this.productDetails = { image_urls: [] } as Product;
    this.originalProduct = { image_urls: [] } as Product;
    this.categories = [];
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
    this.openSections = {
      product: true,
      sourcing: false,
      seo: false,
      media: false,
    };
    this.activityLogs = [];
  };

  toggleSection = (section: keyof typeof this.openSections) => {
    this.openSections[section] = !this.openSections[section];
  };

  setLoadingAndError = action((errorMessage: string | null) => {
    this.error = errorMessage;
  });

  getCategories = async () => {
    try {
      this.categories = await this.productAPI.getCategories();
    } catch (error: any) {}
  };

  getProductOptions = async () => {
    try {
      this.productOptions = await this.productAPI.getProductOptions();
    } catch (error: any) {}
  };

  getProducts = async (params: GetProductsParams) => {
    try {
      const response = await this.productAPI.getProducts(params);
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.products = response.list;
    } catch (error: any) {
      this.error =
        error?.response?.data?.message ||
        "Something went wrong! Please try again later.";
    }
  };

  setProductPodCatalogStatus = (productId: number, inPod: boolean) => {
    this.products = this.products.map((product) =>
      product.id === productId
        ? {
            ...product,
            print_on_demand: inPod,
            in_pod_catalog: inPod,
          }
        : product
    );
    if (this.productDetails.id === productId) {
      this.productDetails = {
        ...this.productDetails,
        print_on_demand: inPod,
        in_pod_catalog: inPod,
      };
    }
  };

  getProductDetailsById = async (productId: number) => {
    try {
      const response = await this.productAPI.getProductDetailsById(productId);
      this.updateLocalProductDetails(response);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  updateVariantImage = async () => {
    try {
      const response = await this.productAPI.updateVariantImage(
        this.productDetails,
      );
      this.originalProduct = this.productDetails;
      return response.result;
    } catch (error: any) {
      throw error;
    }
  };

  clearProductDetails = () => {
    this.originalProduct = {
      variants: [{ options_id: 1, title: "OneSize", value: "OneSize" }],
    };
    this.productDetails = this.originalProduct;
  };

  updateLocalProductDetails = (product: Product) => {
    this.productDetails = product;
    this.originalProduct = product;
  };

  updateName = (name: string) => {
    this.productDetails.name = name;
  };

  updateSEOTitle = (seoTitle: string) => {
    this.productDetails.seo_title = seoTitle;
  };

  updateSEODescription = (description: string) => {
    this.productDetails.seo_description = description;
  };

  updateSKU = (sku?: string) => {
    this.productDetails.sku = sku;
  };

  updateCostPrice = (costPrice: number) => {
    if (isNaN(costPrice)) return;
    this.productDetails.cost_price = costPrice;
  };

  updateSellingPrice = (sellingPrice: number) => {
    if (isNaN(sellingPrice)) return;
    this.productDetails.selling_price = sellingPrice;
  };
  updateDescription = (desc: string) => {
    if (desc !== undefined) this.productDetails.desc = desc;
  };
  updateCategoryName = (categoryName: string) => {
    this.productDetails.category_name = categoryName;
  };
  updateHSNCode = (hsnCode: string) => {
    this.productDetails.hsn_code = hsnCode;
  };

  updateProductStatus = (activeProduct: boolean) => {
    this.productDetails.active = activeProduct;
  };

  updateDisplay = (display: boolean) => {
    if (display !== null) this.productDetails.display = display;
  };

  updateFastProcurement = (fastProcurement: boolean) => {
    this.productDetails.fast_procurement = fastProcurement;
  };

  updatePreferredPrintingTypeId = (preferredPrintingTypeId: number | null) => {
    this.productDetails.preferred_printing_type_id = preferredPrintingTypeId;
  };

  updatePreferredVendorId = (preferredVendorId: number | null) => {
    this.productDetails.preferred_vendor_id = preferredVendorId;
  };

  updateZohoID = (zohoId: string) => {
    if (zohoId !== null) this.productDetails.zoho_id = zohoId;
  };

  updateGSTCode = (gst: number) => {
    this.productDetails.gst_percentage = gst;
  };

  addImages = (newImages: ProductImage[]) => {
    if (newImages.length === 0) return;
    if (!this.productDetails.image_urls) {
      this.productDetails.image_urls = [];
    }

    // Filter out images that already exist to prevent duplicates
    const existingUrls = this.productDetails.image_urls.map((img) => img.url);
    const uniqueNewImages = newImages.filter(
      (img) => !existingUrls.includes(img.url),
    );

    if (uniqueNewImages.length > 0) {
      this.productDetails.image_urls.push(...uniqueNewImages);
    }
  };

  removeImage = (image: ProductImage) => {
    const newImages = this.productDetails.image_urls?.filter(
      (img) => img.url !== image.url,
    );
    this.productDetails.image_urls = newImages || [];
  };

  updatedVendors = (selectedVendors: FilterValue[]) => {
    const vendors: Vendor[] = selectedVendors.map((user) => {
      return {
        id: Number(user.id),
        name: user.value,
      } as Vendor;
    });

    this.productDetails.vendors = vendors;
    if (
      this.productDetails.preferred_vendor_id != null &&
      !vendors.some((vendor) => vendor.id === this.productDetails.preferred_vendor_id)
    ) {
      this.productDetails.preferred_vendor_id = null;
    }
  };

  updateCategory = (selectedValues?: FilterValue[]) => {
    if (selectedValues && selectedValues.length !== 0) {
      const firstCategory = selectedValues[0];
      const updatedCategories = selectedValues.map(({ id, value }) => ({
        category_id: Number(id),
        category_name: value || "",
      }));
      this.productDetails = {
        ...this.productDetails,
        category_id: Number(firstCategory.id),
        category_name: firstCategory.value,
        categories: updatedCategories,
      };
    } else {
      this.productDetails = {
        ...this.productDetails,
        category_id: undefined,
        category_name: undefined,
        categories: [],
      };
    }
  };

  updateProductOptions = (variants: ProductVariant[]) => {
    this.productDetails = {
      ...this.productDetails,
      variants: variants,
    };
  };

  resetProductOptions = () => {
    this.productDetails = {
      ...this.productDetails,
      variants: this.originalProduct.variants
        ? [...this.originalProduct.variants]
        : [],
    };
  };

  createProduct = async (productDetails: Product) => {
    try {
      const response = await this.productAPI.createProduct(productDetails);
      this.products = response.result.list || [];
      return response.result;
    } catch (error: any) {
      throw error;
    }
  };

  updateProductDetails = async () => {
    try {
      const response = await this.productAPI.updateProductDetails(
        this.productDetails,
      );
      // Create a separate copy to avoid reference sharing
      this.originalProduct = { ...this.productDetails };
      return response.result;
    } catch (error: any) {
      throw error;
    }
  };

  updateProductImages = async (payload?: {
    product: Product;
    size_chart: number;
  }) => {
    try {
      const response = await this.productAPI.updateProductImages(
        payload || { product: this.productDetails, size_chart: 0 },
      );
      // Create a separate copy to avoid reference sharing
      this.productDetails.image_urls = [...response.result];
      this.originalProduct.image_urls = [...response.result];
      return response.success;
    } catch (error: any) {
      throw error;
    }
  };

  addVariantImages = (variantId: number, newImages: ProductVariantImages[]) => {
    if (newImages.length === 0) return;

    const variant = this.productDetails.variants?.find(
      (v) => v.id === variantId,
    );

    if (variant) {
      if (!variant.image_urls) {
        variant.image_urls = [];
      }
      variant.image_urls.push(...newImages);
    }
  };

  removeVariantImage = (image: ProductImage, variantId: number) => {
    if (!variantId) return;

    let variants = this.productDetails.variants || [];

    if (variants.length === 0) return;

    const index = variants.findIndex((variant) => variant.id === variantId);
    if (index === -1) return;

    const variant = variants[index];
    const newImages = variant.image_urls?.filter((url) => url.id !== image.id);

    const updatedVariant = {
      ...variant,
      image_urls: newImages,
    };

    variants[index] = updatedVariant;

    this.productDetails = {
      ...this.productDetails,
      variants: variants,
    };
  };

  discardVariantImagesChanges = (variantId: number) => {
    if (!variantId) return;

    let variants = this.productDetails.variants || [];

    if (variants.length === 0) return;

    const index = variants.findIndex((variant) => variant.id === variantId);
    if (index === -1) return;

    const originalVaraint = this.originalProduct.variants![index];
    if (originalVaraint) {
      variants[index] = originalVaraint;
      this.productDetails.variants = variants;
    }
  };

  discardChanges = () => {
    this.productDetails = { ...this.originalProduct };
  };

  getOrderProductsFromProducts = (products: Product[]): OrderProduct[] => {
    return products.map(
      (product) =>
        ({
          id: product.id || 0,
          name: product.name,
          cost_price: product.cost_price || 0,
          unit_price: product.selling_price || 0,
          quantity: 1,
          discount: 0,
          selling_price: product.selling_price || 0,
          product_selling_price: product.selling_price || 0,
          customisation_required: false,
          mrp: product.cost_price || 0,
          tax_amount: 0,
          amount: 0,
          gst_percentage: product.gst_percentage || 0,
          used_quantity: 0,
          sku: product.sku,
          hsn_code: product.hsn_code,
          category_id: product.category_id || 0,
          category_name: product.category_name,
          desc: product.desc || "",
          zoho_id: product.zoho_id ? parseInt(product.zoho_id) : undefined,
          image_urls: product.image_urls
            ? product.image_urls.map((img) => img.url)
            : undefined,
          image_url: product.image_url,
          active: product.active || false,
          size_chart_url: product.size_chart_url || "",
          stock_by_city: product.stock_by_city || [],
        }) as OrderProduct,
    );
  };

  duplicateProduct = async (id: number) => {
    try {
      const response = await this.productAPI.duplicateProduct(id);
      if (response.success) {
        const findExistingProduct = this.products.find(
          (prod) => prod.id === id,
        );
        if (findExistingProduct) {
          const newProduct = {
            ...findExistingProduct,
            id: response.result,
          };
          this.products = [...this.products, newProduct];
        }
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  addSizeChartImage = (url: string) => {
    if (url.length === 0) return;
    this.productDetails.size_chart_url = url;
  };

  removeSizeChartImage = () => {
    this.productDetails.size_chart_url = "";
  };

  setSelectedSizeChartFile = (file: File | null) => {
    this.selectedSizeChartFile = file;
  };

  clearSelectedSizeChartFile = () => {
    this.selectedSizeChartFile = null;
  };

  getPrintingTypes = async () => {
    try {
      if (this.printingTypes.length === 0) {
        const response = await this.productAPI.getPrintingTypes();
        this.printingTypes = response.result;
      }
    } catch (error: any) {
      throw error;
    }
  };

  updatePrintingType = (selectedValues?: FilterValue[]) => {
    if (selectedValues && selectedValues.length !== 0) {
      const updatedPrintingTypes = selectedValues.map(({ id, value }) => ({
        id: Number(id),
        name: value || "",
      }));
      this.productDetails = {
        ...this.productDetails,
        printing_types: updatedPrintingTypes,
      };
    } else {
      this.productDetails = {
        ...this.productDetails,
        printing_types: [],
      };
    }
  };

  lastSoldProductsDetails = async (productIds: number | number[]) => {
    try {
      const response =
        await this.productAPI.lastSoldProductsDetails(productIds);
      this.lastSoldProducts = response.result;
    } catch (error: any) {
      throw error;
    }
  };

  updateProductSEO = async () => {
    try {
      if (!this.productDetails.id) {
        throw new Error("Product ID is required");
      }

      if (
        !this.productDetails.seo_title ||
        !this.productDetails.seo_description
      ) {
        throw new Error("SEO title and description are required");
      }

      const seoDetails: ProductSEODetails = {
        id: this.productDetails.id,
        seo_title: this.productDetails.seo_title,
        seo_description: this.productDetails.seo_description,
      };

      const response = await this.productAPI.updateProductSEO(seoDetails);
      if (response.success) {
        this.originalProduct.seo_title = this.productDetails.seo_title;
        this.originalProduct.seo_description =
          this.productDetails.seo_description;
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  updateProductSource = async () => {
    try {
      if (!this.productDetails.id) {
        throw new Error("Product ID is required");
      }

      const sourcingData: ProductSourcingDetails = {
        id: this.productDetails.id,
        zoho_id: this.productDetails.zoho_id,
        hsn_code: this.productDetails.hsn_code!,
        printing_types: this.productDetails.printing_types || [],
        preferred_printing_type_id:
          this.productDetails.preferred_printing_type_id ?? null,
        preferred_vendor_id: this.productDetails.preferred_vendor_id ?? null,
        gst_percentage: this.productDetails.gst_percentage!,
        fast_procurement: this.productDetails.fast_procurement || false,
        vendors: this.productDetails.vendors || [],
      };

      const response = await this.productAPI.updateProductSource(sourcingData);

      if (response.success) {
        this.originalProduct.zoho_id = this.productDetails.zoho_id;
        this.originalProduct.hsn_code = this.productDetails.hsn_code;
        this.originalProduct.printing_types =
          this.productDetails.printing_types;
        this.originalProduct.preferred_printing_type_id =
          this.productDetails.preferred_printing_type_id ?? null;
        this.originalProduct.preferred_vendor_id =
          this.productDetails.preferred_vendor_id ?? null;
        this.originalProduct.gst_percentage =
          this.productDetails.gst_percentage;
        this.originalProduct.fast_procurement =
          this.productDetails.fast_procurement;
        this.originalProduct.vendors = this.productDetails.vendors;
      }

      return response;
    } catch (error: any) {
      throw error;
    }
  };

  setDefaultImage = async (imageId: number) => {
    try {
      if (!this.productDetails.id) {
        throw new Error("Product ID is required");
      }

      const response = await this.productAPI.setDefaultImage(
        this.productDetails.id,
        imageId,
      );

      if (response.success) {
        // Update the local state to reflect the new default image
        this.productDetails.image_urls =
          this.productDetails.image_urls?.map((img) => ({
            ...img,
            is_default: img.id === imageId,
          })) || [];

        // Also update the original product to maintain consistency
        this.originalProduct.image_urls =
          this.originalProduct.image_urls?.map((img) => ({
            ...img,
            is_default: img.id === imageId,
          })) || [];
      }

      return response;
    } catch (error: any) {
      throw error;
    }
  };

  fetchPurchaseRequestInsights = async (productId: number) => {
    try {
      const response =
        await this.productAPI.getPurchaseRequestInsights(productId);
      this.purchaseRequestInsights =
        response.result as PurchaseRequestProductInsights[];
    } catch (error: any) {
      throw error;
    }
  };

  getProductActivityLogs = async (productId: number) => {
    try {
      const response = await this.productAPI.getProductActivityLogs(productId);
      this.activityLogs = response;
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  clearActivityLogs = () => {
    this.activityLogs = [];
  };
}
